/**
 * ChartCard + ChartFrame — canonical chart-card primitives.
 *
 * Mockup parity: every chart in
 * `reignara_redesign/app/dashboard/page.tsx` ships inside a
 * rounded-2xl card with a header strip (eyebrow label + optional
 * affordance) and a fixed-height responsive Recharts container. The
 * mockup uses two specific chart styles:
 *
 *   - LineChart with `tick={{ fontSize: 11, fill: muted-gray }}`,
 *     a soft `CartesianGrid` (vertical=false), and a single
 *     primary-tinted line.
 *   - AreaChart with the same axis style, a soft grid, and a single
 *     primary-tinted area gradient.
 *
 * `<ChartCard>` is the chrome (rounded card + header). `<ChartFrame>`
 * is the inner Recharts shell — it provides the
 * `ResponsiveContainer`, fixed default height, and styled axes /
 * tooltip / grid that any chart variant (line/area/bar) plugs into.
 *
 * Consumers compose:
 *
 *   <ChartCard eyebrow="Scholarship Accrual">
 *     <ChartFrame>
 *       <AreaChart data={data}>
 *         <XAxis dataKey="month" />
 *         <YAxis />
 *         <Tooltip />
 *         <Area dataKey="value" stroke="var(--primary)"
 *               fill="var(--primary)" fillOpacity={0.15} />
 *       </AreaChart>
 *     </ChartFrame>
 *   </ChartCard>
 *
 * The axis tick / grid / tooltip are styled centrally in
 * `<ChartFrame>` via cloneElement-style children, so consumers get
 * mockup-correct axis styling for free.
 *
 * Spec: meridian-redesign — PR (m.6) Phase 1 + PR (m.8) extended
 * Requirements: 1.5, 14.1, 18.5
 */

import { Children, cloneElement, isValidElement, type JSX, type ReactElement, type ReactNode } from 'react';
import { CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { cn } from '@/lib/utils';

// --- ChartCard ---------------------------------------------------------------

export interface ChartCardProps {
  /** Eyebrow label rendered at the top of the card. */
  eyebrow: string;
  /** Optional sub-title rendered below the eyebrow (display-Bavex). */
  title?: ReactNode;
  /** Optional right-aligned action (legend, range picker, etc.). */
  action?: ReactNode;
  /** Chart body — typically a `<ChartFrame>` with a Recharts chart inside. */
  children: ReactNode;
  /** Optional footer caption (small, muted) — e.g. data attribution. */
  caption?: ReactNode;
  /** Extra classes merged via `cn()`. */
  className?: string;
  /** Stable test id; defaults to `chart-card`. */
  'data-testid'?: string;
}

export function ChartCard({
  eyebrow,
  title,
  action,
  children,
  caption,
  className,
  'data-testid': dataTestId,
}: ChartCardProps): JSX.Element {
  return (
    <div
      data-testid={dataTestId ?? 'chart-card'}
      className={cn(
        'rounded-card border border-border bg-card shadow-subtle p-card-comfortable',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <span className="text-eyebrow-sm text-muted-foreground">
            {eyebrow}
          </span>
          {title ? (
            <p className="mt-1 font-display text-page-title text-foreground ">
              {title}
            </p>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
      {caption ? (
        <p className="mt-3 font-sans text-body-sm text-muted-foreground">
          {caption}
        </p>
      ) : null}
    </div>
  );
}

// --- ChartFrame --------------------------------------------------------------

const AXIS_TICK = { fontSize: 11, fill: 'var(--muted-foreground)' } as const;
const TOOLTIP_CONTENT_STYLE = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: '0.75rem',
  fontSize: '12px',
  color: 'var(--foreground)',
  boxShadow: 'var(--shadow-raised)',
} as const;

export interface ChartFrameProps {
  /** A single Recharts chart element (LineChart / AreaChart / BarChart / etc.).
   *  XAxis, YAxis, Tooltip, and CartesianGrid are auto-styled into
   *  the chart — consumers don't need to repeat the styling. */
  children: ReactElement;
  /** Fixed height in px. Defaults to 240. */
  height?: number;
  /** Whether to render a soft horizontal grid. Defaults to true. */
  showGrid?: boolean;
  /** Hide the default Tooltip — pass false when a custom tooltip is
   *  supplied as a child of the chart. Defaults to true. */
  showTooltip?: boolean;
}

/**
 * Walks a Recharts chart's children and adds the canonical axis /
 * tooltip / grid props if those elements aren't already provided by
 * the consumer. This keeps consumer code declarative
 * (`<XAxis dataKey="month" />`) while guaranteeing axis tick
 * styling matches the mockup.
 */
function styleChart(chart: ReactElement, opts: { showGrid: boolean; showTooltip: boolean }): ReactElement {
  const enhanced = Children.map(
    (chart.props as { children?: ReactNode }).children,
    (child) => {
      if (!isValidElement(child)) return child;
      const t = child.type as { displayName?: string; name?: string };
      const tag = t?.displayName ?? t?.name ?? '';
      if (tag === 'XAxis' || tag === 'YAxis') {
        const existing = child.props as Record<string, unknown>;
        return cloneElement(child as ReactElement<Record<string, unknown>>, {
          tick: existing.tick ?? AXIS_TICK,
          stroke: existing.stroke ?? 'var(--border)',
          tickLine: existing.tickLine ?? false,
          axisLine: existing.axisLine ?? false,
        });
      }
      if (tag === 'Tooltip') {
        const existing = child.props as Record<string, unknown>;
        return cloneElement(child as ReactElement<Record<string, unknown>>, {
          contentStyle: existing.contentStyle ?? TOOLTIP_CONTENT_STYLE,
          cursor: existing.cursor ?? { stroke: 'var(--border)', strokeWidth: 1 },
        });
      }
      return child;
    },
  );

  // Inject default Tooltip if missing and showTooltip is true.
  const hasTooltip = enhanced?.some(
    (c) => isValidElement(c) && ((c.type as { displayName?: string; name?: string })?.displayName ?? (c.type as { displayName?: string; name?: string })?.name) === 'Tooltip',
  );
  const hasGrid = enhanced?.some(
    (c) => isValidElement(c) && ((c.type as { displayName?: string; name?: string })?.displayName ?? (c.type as { displayName?: string; name?: string })?.name) === 'CartesianGrid',
  );

  const injected: ReactNode[] = [];
  if (opts.showGrid && !hasGrid) {
    injected.push(
      <CartesianGrid
        key="__grid"
        stroke="var(--border)"
        strokeDasharray="3 3"
        vertical={false}
      />,
    );
  }
  if (opts.showTooltip && !hasTooltip) {
    injected.push(
      <Tooltip
        key="__tooltip"
        contentStyle={TOOLTIP_CONTENT_STYLE}
        cursor={{ stroke: 'var(--border)', strokeWidth: 1 }}
      />,
    );
  }

  return cloneElement(chart, undefined, [
    ...injected,
    ...(enhanced ?? []),
  ]);
}

export function ChartFrame({
  children,
  height = 240,
  showGrid = true,
  showTooltip = true,
}: ChartFrameProps): JSX.Element {
  const styled = styleChart(children, { showGrid, showTooltip });
  return (
    <div data-testid="chart-frame" style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        {styled}
      </ResponsiveContainer>
    </div>
  );
}

// Re-export the underlying axis/tooltip pieces so consumers can
// import them all from one place.
export { CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis };
